import type { Dictionary } from '@libs/core';
import type { PersonalizeFormInputsDictionaryItem } from '@app/pdp/components/PersonalizedProduct';
import type { ProductItem } from '@libs/common-next';

/**
 * This function normalize the data between personalize labels and services
 *
 * @param inputs contains the data for front or back custom labels
 * @returns object that contains the labels and values separated by pipes.
 */
export const parseCustomizationLabelAndValues = (
    inputs: Dictionary<PersonalizeFormInputsDictionaryItem[]>,
    isExperimentFullSolution = false
): Partial<ProductItem> => {
    const frontInputs = Object.values(inputs.front || {});
    const backInputs = Object.values(inputs.back || {});

    const formatLabel = (
        side: string,
        { label, maxLength }: PersonalizeFormInputsDictionaryItem
    ) => {
        const PREFIX_LABEL = 'Engraved Text';
        const POSTFIX_LABEL = maxLength ? ` (Max ${maxLength})` : '';

        return `${PREFIX_LABEL} (${side}) ${label}${POSTFIX_LABEL}`;
    };

    let labels = '';
    let values = '';

    if (isExperimentFullSolution) {
        labels = [
            ...frontInputs.map(input => formatLabel('FRONT', input)),
            ...backInputs.map(input => formatLabel('BACK', input)),
        ].join('|');

        values = [
            ...frontInputs.map(input => input.value ?? ''),
            ...backInputs.map(input => input.value ?? ''),
        ].join('|');
    } else {
        labels = [
            frontInputs.map(input => formatLabel('FRONT', input)).join('|'),
            backInputs.map(input => formatLabel('BACK', input)).join('|'),
        ].join('|');

        values = [
            frontInputs.map(input => input.value ?? '').join('|'),
            backInputs.map(input => input.value ?? '').join('|'),
        ].join('|');
    }

    const textKey = isExperimentFullSolution
        ? 'customizationVasText'
        : 'c_customizationVasText';
    const labelKey = isExperimentFullSolution
        ? 'customizationVasTextLabel'
        : 'c_customizationVasTextLabel';

    return {
        [textKey]: values ? `|${values}|` : '',
        [labelKey]: labels ? `|${labels}|` : '',
    };
};
